import { useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Autoplaying feature film inside a pillar card. Captions are synced to the film's scene boundaries
 * (one caption per `segment` seconds); the speaker toggles the audio bed (music / narration).
 */
export default function PillarShowcase({ video, captions = [], segment = 10, label, testid }) {
  const ref = useRef(null);
  const [sound, setSound] = useState(false);
  const [scene, setScene] = useState(0);

  const toggle = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    v.muted = sound;
    if (!sound) v.play().catch(() => {});
    setSound(!sound);
  };

  const onTime = () => {
    const v = ref.current;
    if (!v || !captions.length) return;
    setScene(Math.min(Math.floor(v.currentTime / segment), captions.length - 1));
  };

  return (
    <>
      <video
        ref={ref}
        className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:scale-105 transition-transform duration-700"
        poster={video.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        onTimeUpdate={onTime}
        data-testid={`${testid}-video`}
      >
        <source src={video.src} type="video/mp4" />
        <source src={video.webm} type="video/webm" />
      </video>
      <button
        type="button"
        onClick={toggle}
        aria-pressed={sound}
        aria-label={sound ? "Mute" : "Play with sound"}
        className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
          sound ? "bg-[#FF1E8C] border-[#FF1E8C] text-white" : "bg-[#061536]/85 border-white/20 text-white/85 hover:border-[#FF1E8C]/70"
        }`}
        data-testid={`${testid}-sound-toggle`}
      >
        {sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>
      {captions.length > 0 && (
        <span
          key={scene}
          className="absolute top-3 left-3 z-10 font-mono-lbl text-[10px] uppercase tracking-wider text-[#061536] bg-[#00F0FF] px-2.5 py-1 rounded font-bold"
          style={{ animation: "d1-fade 0.4s both" }}
          data-testid={`${testid}-caption`}
        >
          {captions[scene]}
        </span>
      )}
      <span className="absolute bottom-3 right-3 z-10 font-mono-lbl text-[10px] text-white/80 bg-[#061536]/85 px-2.5 py-1 rounded flex items-center gap-1.5" data-testid={`${testid}-label`}>
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF2E2E] animate-pulse" /> {label} · {sound ? "Sound on" : "Tap speaker"}
      </span>
    </>
  );
}
